import { useMemo, useState } from 'react';
import {
  TeBadge,
  TeButton,
  TeButtonMinimal,
  TeContainer,
  TeContainerSlim,
  TeDivider,
  TeIcon,
  TePopover,
  TeText,
} from '@topcon/tuicl-react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useAuthentication, useNotification } from '@topcon/topcon-utils';

import {
  useAcceptInviteToJoin,
  useRejectInviteToJoin,
  useCreateRequestToJoin,
  useDeleteRequestToJoin,
} from '../../pages/Companies/companies.mutations';
import { useErrorDrawer } from '../../providers/ErrorDrawerContext/error-drawer.context';
import { background, border } from '../../providers/theme/theme.index';
import { getErrorStatus } from '../../utils/errorStatus';
import {
  BusinessPartnerAccessabilityEnum,
  BusinessPartnerWithCurrentIdentityInfoBody,
  RequestToJoinPostBody,
} from '../../api/generated/pap';
import { RequestBusinessPartnerBody } from '../../api/generated/pm';
import CompanyIcon from './CompanyIcon';
import RequestToJoinInfoPopover from './RequestToJoinInfoPopover';
import CustomBadge from '../common/CustomBadge/CustomBadge';
import {
  formatOrganizationNumber,
  isValidOrganizationNumber,
} from '../../utils/organizationNumberFormatter';
import { BusinessPartnerType } from '../../app/routes/utils';
import { responsiveColumnStack } from '../styles/responsive';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { BREAKPOINTS } from '../../utils/breakpoints';

/**
 * One tree row: a Business Partner the identity belongs to, or a pending
 * Business Partner Request shaped like one. Only the request carries an address
 * (the PAP Business Partner body has none), which the cancel dialog shows.
 */
export type CompanyTreeItem = Partial<BusinessPartnerWithCurrentIdentityInfoBody> &
  Partial<Pick<RequestBusinessPartnerBody, 'companyAddress'>>;

interface Props {
  type?: 'companies' | 'divisions' | 'inviteToJoin' | 'requestToJoin';
  companies: CompanyTreeItem[];
  searchValue?: string;
  showRoles?: boolean;
  allowSignIn?: boolean;
  onUpdate?: () => void;
  onCancelRequest?: (company: CompanyTreeItem, isCompany: boolean) => void;
}

function CompanyTree({
  type,
  companies,
  showRoles = false,
  allowSignIn = false,
  onUpdate,
  onCancelRequest,
}: Props) {
  const { t: commonT } = useTranslation('common');
  const { t } = useTranslation(['companies', 'divisions', 'common', 'profile']);
  const { mutateAsync: acceptInviteToJoin } = useAcceptInviteToJoin();
  const { mutateAsync: rejectInviteToJoin } = useRejectInviteToJoin();
  const { mutateAsync: cancelRequestToJoin } = useDeleteRequestToJoin();
  const { mutateAsync: requestToJoin } = useCreateRequestToJoin();
  const notificationService = useNotification();
  const { showError } = useErrorDrawer();
  const authenticationService = useAuthentication();
  const [pendingAcceptIds, setPendingAcceptIds] = useState(() => new Set<string>());
  const [pendingRejectIds, setPendingRejectIds] = useState(() => new Set<string>());
  const userId = authenticationService.identityId;
  const isMobile = useMediaQuery('(max-width: 768px)');

  const companyMap = useMemo(
    () =>
      companies.reduce(
        (acc, company) => {
          if (company.id) {
            acc[company.id] = company;
          }
          return acc;
        },
        {} as Record<string, CompanyTreeItem>
      ),
    [companies]
  );
  const parentToChildren = useMemo(
    () =>
      companies.reduce(
        (acc, { id }) => {
          if (!id) return acc;
          const childIds = companies
            .filter(({ parentBusinessPartnerId }) => id === parentBusinessPartnerId)
            .map((child) => child.id)
            .filter((id): id is string => !!id);
          if (childIds.length) acc[id] = childIds;
          return acc;
        },
        {} as Record<string, string[]>
      ),
    [companies]
  );
  const rootCompanies = useMemo(() => {
    const childSet = new Set<string>();
    Object.values(parentToChildren).forEach((childIds: string[]) =>
      childIds.forEach((childId: string) => childSet.add(childId))
    );
    return companies.filter(({ id }) => !childSet.has(id ?? ''));
  }, [companies, parentToChildren]);

  const acceptInvite = async (id: string, name: string) => {
    try {
      setPendingAcceptIds((prev) => new Set([...prev, id]));
      await acceptInviteToJoin(id);
    } catch (error: unknown) {
      if (!showError(error)) {
        let errorMessage: string;

        // An API error response is the parsed body with the HTTP status
        // attached by the shared interceptor; its `message` is the backend's
        // reason. Anything else (e.g. a network failure) keeps the generic copy.
        if (
          getErrorStatus(error) !== undefined &&
          typeof error === 'object' &&
          error !== null &&
          'message' in error &&
          typeof error.message === 'string'
        ) {
          errorMessage = t('inviteSection.inviteAcceptedFailedDescriptionWithReason', {
            ns: 'companies',
            reason: error.message,
            name,
          });
        } else {
          errorMessage = t('inviteSection.inviteAcceptedFailedDescription', {
            ns: 'companies',
            name,
          });
        }

        notificationService.addNotification(
          t('inviteSection.inviteAcceptedFailed', { ns: 'companies', name }),
          errorMessage,
          'error'
        );
      }
    } finally {
      setPendingAcceptIds((prev) => {
        prev.delete(id);
        return new Set(prev);
      });
    }

    onUpdate?.();
  };

  const rejectInvite = async (id: string, name: string) => {
    try {
      setPendingRejectIds((prev) => new Set([...prev, id]));
      await rejectInviteToJoin(id);
    } catch {
      notificationService.addNotification(
        t('inviteSection.inviteRejectedFailed', { ns: 'companies' }),
        t('inviteSection.inviteRejectedFailedDescription', { ns: 'companies', name }),
        'error'
      );
    } finally {
      setPendingRejectIds((prev) => {
        prev.delete(id);
        return new Set(prev);
      });
    }
    onUpdate?.();
  };

  const cancelRequest = async (id: string, name: string) => {
    try {
      await cancelRequestToJoin(id);
    } catch {
      notificationService.addNotification(
        t('requestCanceledFailed'),
        t('requestCanceledFailedDescription', { name }),
        'error'
      );
    }

    onUpdate?.();
  };

  const switchCompany = (bpId?: string) => {
    if (!bpId) return;
    // silently set the current URL to home (no navigation) so the auth redirect
    // will use the safe path as the 'redirect_url' / currentRelativeURL
    try {
      window.history.replaceState(null, '', '/');
    } catch {
      // ignore if replaceState unavailable
    }

    // Build the login URL including the bp id and navigate
    const loginUrl = authenticationService.getLoginURL(true, bpId);
    window.location.assign(loginUrl);
  };

  const joinRequest = async (businessPartnerId?: string, name?: string) => {
    try {
      await requestToJoin({
        businessPartnerId,
      } as RequestToJoinPostBody);
    } catch {
      notificationService.addNotification(
        t('requestToJoinFailed', { ns: 'common' }),
        t('requestToJoinFailedDescription', { ns: 'common', name }),
        'error'
      );
    }
    onUpdate?.();
  };

  const renderInviteOnlyInfo = (companyId: string) => {
    const popoverId = `invite-only-info-${companyId}`;
    return (
      <>
        <TeButton variant="neutral" size="s" icon="info" popkey={popoverId} />
        <TePopover for={popoverId} variant="secondary" operation="hover" placement="right">
          <TeContainerSlim padding={['xs', 'm']} style={{ maxWidth: 280 }}>
            <TeText color="text.inverse" size={14}>
              {t('inviteOnlyInfo', { ns: 'divisions' })}
            </TeText>
          </TeContainerSlim>
        </TePopover>
      </>
    );
  };

  const renderRequestToJoinInfo = (companyId: string, requestToJoinId?: string) => {
    return <RequestToJoinInfoPopover companyId={companyId} requestToJoinId={requestToJoinId} />;
  };

  const renderButtons = (
    company: CompanyTreeItem & { inviteId?: string },
    id: string,
    name: string,
    isPending: boolean,
    isCompany: boolean
  ) => {
    const isDivision = type === 'divisions' || !!company.parentBusinessPartnerId;
    const inviteId = company.inviteId ?? id;
    const signInButton = allowSignIn &&
      !company.requestToJoin &&
      (company.user || company.isOwner) && (
        <TeButtonMinimal
          text={t(isDivision ? 'signInToDivision' : 'signInToCompany', { ns: 'common' })}
          postfixIcon="arrow_forward"
          size="xs"
          teclick={() => switchCompany(company.id)}
        />
      );

    switch (type) {
      case 'inviteToJoin':
        return (
          <>
            <InviteButtonsRow gap="s" alignItems="center" fill>
              <TeButton
                variant="neutral"
                size="s"
                text={t('inviteSection.rejectInvite', { ns: 'companies' })}
                disabled={pendingAcceptIds.has(id)}
                loading={pendingRejectIds.has(id)}
                teclick={() => {
                  rejectInvite(inviteId, name);
                }}
                grow
              />
              <TeButton
                variant="neutral"
                size="s"
                text={t('inviteSection.acceptInvite', { ns: 'companies' })}
                disabled={pendingRejectIds.has(id)}
                loading={pendingAcceptIds.has(id)}
                teclick={() => {
                  acceptInvite(inviteId, name);
                }}
                grow
              />
            </InviteButtonsRow>
            {signInButton}
          </>
        );
      case 'requestToJoin':
        return (
          <TeContainerSlim gap="xs" alignItems="center">
            {renderRequestToJoinInfo(id, company.requestToJoin?.id)}
            <TeButton
              variant="neutral"
              size="s"
              text={t('cancelRequestToJoin', { ns: 'common' })}
              teclick={() => {
                cancelRequest(company.requestToJoin?.id ?? '', name);
              }}
            />
            {signInButton}
          </TeContainerSlim>
        );

      case 'companies':
        return (
          <TeContainerSlim gap="xs" alignItems="center">
            {isPending && (
              <>
                {renderRequestToJoinInfo(id, company.requestToJoin?.id)}
                <TeButton
                  variant="neutral"
                  size="s"
                  text={t('cancelRequestToJoin', { ns: 'common' })}
                  teclick={() => onCancelRequest?.(company, isCompany)}
                />
              </>
            )}
            {signInButton}
          </TeContainerSlim>
        );

      case 'divisions': {
        const isInviteOnly = company.accessability === BusinessPartnerAccessabilityEnum.INVITE_ONLY;
        const showRequestToJoinButton =
          !isPending &&
          !isInviteOnly &&
          !company.isOwner &&
          userId !== company?.ownerIdentityId &&
          userId !== company?.user?.identityId &&
          userId !== company?.requestToJoin?.identityId;
        const showInviteOnlyInfo = isInviteOnly && !company.user;
        return (
          <>
            {isPending && userId === company?.requestToJoin?.identityId ? (
              <TeContainerSlim gap="xs" alignItems="center">
                {renderRequestToJoinInfo(id, company.requestToJoin?.id)}
                <TeButton
                  variant="neutral"
                  size="s"
                  text={t('cancelRequestToJoin', { ns: 'common' })}
                  teclick={() => cancelRequest(company.requestToJoin?.id ?? '', name)}
                />
              </TeContainerSlim>
            ) : (
              showRequestToJoinButton && (
                <TeButton
                  variant="neutral"
                  size="s"
                  text={t('requestToJoin', { ns: 'common' })}
                  teclick={() => joinRequest(company.id, company.name)}
                />
              )
            )}
            {showInviteOnlyInfo && renderInviteOnlyInfo(id)}
            {!showRequestToJoinButton && signInButton}
          </>
        );
      }
    }
  };

  const renderStatus = (company: CompanyTreeItem, hasRequestToJoin: boolean) => {
    if (company.user || company.isOwner || userId === company.ownerIdentityId) return null;

    if (company.accessability === BusinessPartnerAccessabilityEnum.INVITE_ONLY) {
      return (
        <TeText size={12} weight={400} color="text.secondary">
          {t('inviteOnly', { ns: 'divisions' })}
        </TeText>
      );
    }

    if (company.accessability === BusinessPartnerAccessabilityEnum.REQUEST_TO_JOIN) {
      return (
        <TeText size={12} weight={400} color="text.secondary">
          {hasRequestToJoin
            ? t('requestedToJoin', { ns: 'common' })
            : t('notJoined', { ns: 'common' })}
        </TeText>
      );
    }

    return null;
  };

  const renderItem = (company: CompanyTreeItem, hasRequestToJoin: boolean) => {
    const renderCompanyTypeLabel = () => {
      switch (company.type) {
        case BusinessPartnerType.Dealer:
          return 'dealer';
        case BusinessPartnerType.Topcon:
          return 'topcon';
        case BusinessPartnerType.EndCustomerIndividual:
        case BusinessPartnerType.EndCustomerEnterprise:
        default:
          return 'customer';
      }
    };

    const renderCompanyTypeIcon = () => {
      switch (company.type) {
        case BusinessPartnerType.Dealer:
          return 'handshake';
        case BusinessPartnerType.Topcon:
        case BusinessPartnerType.EndCustomerIndividual:
        case BusinessPartnerType.EndCustomerEnterprise:
        default:
          return 'domain';
      }
    };

    return (
      <TeContainerSlim gap="xxs4" alignItems="flex-start" column>
        <CompanyNameBadgeContainer gap="xs" alignItems="center">
          <TeContainerSlim alignItems="center" gap="xs">
            {company.accessability == BusinessPartnerAccessabilityEnum.INVITE_ONLY && (
              <TeIcon name="lock" />
            )}
            <TeText size={16} weight={500} color="text.primary">
              {company.name}
            </TeText>
          </TeContainerSlim>
          {company.extId && isValidOrganizationNumber(company.extId) && (
            <CustomBadge
              backgroundColor="background.primary"
              borderColor="border.secondary"
              textColor="text.primary"
              size="s"
              text={formatOrganizationNumber(company.extId)}
            />
          )}
        </CompanyNameBadgeContainer>
        <TeContainerSlim gap="xs">
          {!company.parentBusinessPartnerId && (
            <CustomBadge
              backgroundColor="background.tertiary"
              borderColor="border.secondary"
              textColor="text.primary"
              size="s"
              text={commonT(renderCompanyTypeLabel())}
              icon={renderCompanyTypeIcon()}
            />
          )}
          {showRoles && (
            <>
              {(company.isOwner || userId === company.ownerIdentityId) && (
                <TeBadge
                  size="s"
                  backgroundColor="background.inverse"
                  text={t('owner', { ns: 'common' })}
                />
              )}
              {company.user?.profileIds?.map((profileId: string) => {
                return (
                  <TeBadge
                    key={profileId}
                    size="s"
                    backgroundColor="background.primary"
                    textColor="text.primary"
                    text={t(profileId, { ns: 'profile' })}
                  />
                );
              })}
            </>
          )}
        </TeContainerSlim>
        {renderStatus(company, hasRequestToJoin)}
      </TeContainerSlim>
    );
  };

  return (
    <TeContainerSlim column fill gap="s" overflow="auto">
      {rootCompanies.map((company) => {
        const { id, name, requestToJoin } = company;
        const isPending = !!requestToJoin;
        const children = parentToChildren[id ?? ''];
        return (
          <TeContainerSlim
            key={id}
            column
            fill
            borderRadius="l"
            borderColor={border.secondary}
            overflow="hidden"
          >
            <ResponsiveTeContainer alignItems="center" fill justifyContent="space-between">
              <TeContainerSlim gap="m" alignItems="center" fill>
                <CompanyIcon size={isMobile ? 'xs' : 'l'} iconURL={company.iconURL} />
                {renderItem(company, isPending)}
              </TeContainerSlim>
              <ResponsiveButtonsContainer alignItems="center" gap="s">
                {renderButtons(company, id ?? '', name ?? '', isPending, true)}
              </ResponsiveButtonsContainer>
            </ResponsiveTeContainer>
            {children &&
              children.map((companyId: string, index: number) => {
                const foundCompany = companyMap[companyId];
                if (!foundCompany) return null;
                const { name, requestToJoin } = foundCompany;
                const hasRequestToJoin = !!requestToJoin;
                return (
                  <div key={companyId} data-testid={`company-tree-item-${companyId}-child`}>
                    <TeDivider dividerColor="border.secondary" />
                    <ChildTeContainer
                      alignItems="center"
                      padding={['m', 'm', 'm', 'xs']}
                      gap="s"
                      fill
                      relative
                    >
                      <VerticalLine $index={index} />
                      <HorizontalLine />
                      <ResponsiveChildContent
                        alignItems="center"
                        grow
                        justifyContent="space-between"
                        gap="m"
                      >
                        <TeContainerSlim gap="m" alignItems="center">
                          <CompanyIcon
                            size={isMobile ? 'xs' : 'm'}
                            iconURL={foundCompany.iconURL}
                          />
                          {renderItem(foundCompany, hasRequestToJoin)}
                        </TeContainerSlim>
                        <ResponsiveButtonsContainer alignItems="center" gap="s">
                          {renderButtons(
                            foundCompany,
                            companyId ?? '',
                            name ?? '',
                            hasRequestToJoin,
                            false
                          )}
                        </ResponsiveButtonsContainer>
                      </ResponsiveChildContent>
                    </ChildTeContainer>
                  </div>
                );
              })}
          </TeContainerSlim>
        );
      })}
    </TeContainerSlim>
  );
}

export default CompanyTree;

const VerticalLine = styled.div<{ $index: number }>`
  position: absolute;
  width: 1px;
  height: 64px;
  top: 0px;
  background-color: ${border.primary};
  border-radius: 1px;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    top: 0px;
    height: calc(100% - 0px);
  }
`;

const ResponsiveTeContainer = styled(TeContainer)`
  &:hover {
    background-color: ${background.hover.secondary} !important;
  }
  background-color: ${background.secondary} !important;
  @media (max-width: ${BREAKPOINTS.mobile}) {
    ${responsiveColumnStack}

    & > * {
      width: 100% !important;
    }
  }
`;

const ChildTeContainer = styled(TeContainer)`
  background-color: ${background.secondary} !important;
  min-height: 104px;
`;

const ResponsiveButtonsContainer = styled(TeContainerSlim)`
  @media (max-width: ${BREAKPOINTS.mobile}) {
    ${responsiveColumnStack}
    width: 100% !important;

    & > * {
      width: 100% !important;
    }
  }
`;

const ResponsiveChildContent = styled(TeContainerSlim)`
  flex-wrap: wrap;
  @media (max-width: ${BREAKPOINTS.mobile}) {
    ${responsiveColumnStack}

    & > * {
      width: 100% !important;
    }
  }
`;

const InviteButtonsRow = styled(TeContainerSlim)`
  te-button {
    width: 100%;
  }
`;

const HorizontalLine = styled.div`
  width: 14px;
  height: 1px;
  background-color: ${background.disabled};
  border-radius: 1px;
`;

const CompanyNameBadgeContainer = styled(TeContainerSlim)`
  @media (max-width: ${BREAKPOINTS.mobile_small}) {
    ${responsiveColumnStack}
  }
`;
